import { useState, useCallback } from 'react';
import { annotationApi } from '../api/annotationApi';
import { FigmaAnnotationRequest, FigmaAnnotationResponse } from '../types/annotation';

interface UseAnnotationsReturn {
  annotations: FigmaAnnotationResponse[];
  loading: boolean;
  error: string | null;
  loadAnnotations: (fileKey: string, frameId?: string) => Promise<void>;
  createAnnotation: (fileKey: string, request: FigmaAnnotationRequest) => Promise<FigmaAnnotationResponse | null>;
  updateAnnotation: (id: number, request: FigmaAnnotationRequest) => Promise<FigmaAnnotationResponse | null>;
  deleteAnnotation: (id: number) => Promise<boolean>;
  clearAnnotations: () => void;
}

export function useAnnotations(): UseAnnotationsReturn {
  const [annotations, setAnnotations] = useState<FigmaAnnotationResponse[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadAnnotations = useCallback(async (fileKey: string, frameId?: string) => {
    setLoading(true);
    setError(null);
    try {
      const data = await annotationApi.getAnnotations(fileKey, frameId);
      setAnnotations(data);
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Failed to load annotations');
    } finally {
      setLoading(false);
    }
  }, []);

  const createAnnotation = useCallback(async (fileKey: string, request: FigmaAnnotationRequest) => {
    try {
      const created = await annotationApi.createAnnotation(fileKey, request);
      setAnnotations(prev => [...prev, created]);
      return created;
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Failed to create annotation');
      return null;
    }
  }, []);

  const updateAnnotation = useCallback(async (id: number, request: FigmaAnnotationRequest) => {
    try {
      const updated = await annotationApi.updateAnnotation(id, request);
      setAnnotations(prev => prev.map(a => (a.id === id ? updated : a)));
      return updated;
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Failed to update annotation');
      return null;
    }
  }, []);

  const deleteAnnotation = useCallback(async (id: number) => {
    try {
      await annotationApi.deleteAnnotation(id);
      setAnnotations(prev => prev.filter(a => a.id !== id));
      return true;
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Failed to delete annotation');
      return false;
    }
  }, []);

  const clearAnnotations = useCallback(() => {
    setAnnotations([]);
    setError(null);
  }, []);

  return {
    annotations,
    loading,
    error,
    loadAnnotations,
    createAnnotation,
    updateAnnotation,
    deleteAnnotation,
    clearAnnotations,
  };
}
